import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Star,
    LogOut,
    Check,
    X,
    Trash2,
    Clock,
    Loader2,
    Filter,
} from "lucide-react";
import { authApi, clearToken, getToken, formatApiError } from "@/lib/api";

const FILTERS = [
    { key: "pending", label: "En attente" },
    { key: "approved", label: "Approuvés" },
    { key: "rejected", label: "Rejetés" },
    { key: "all", label: "Tous" },
];

function StatusBadge({ s }) {
    const cfg = {
        pending: {
            cls: "bg-amber-100 text-amber-800 border-amber-200",
            label: "En attente",
        },
        approved: {
            cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
            label: "Approuvé",
        },
        rejected: {
            cls: "bg-rose-100 text-rose-800 border-rose-200",
            label: "Rejeté",
        },
    };
    const c = cfg[s] || cfg.pending;
    return (
        <span
            className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${c.cls}`}
        >
            {c.label}
        </span>
    );
}

export default function AdminDashboard() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!getToken()) {
            nav("/admin/login");
            return;
        }
        load();
        // eslint-disable-next-line
    }, [filter]);

    const load = async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status_filter: filter };
            const [statsRes, reviewsRes] = await Promise.all([
                authApi.get("/admin/reviews/stats"),
                authApi.get("/admin/reviews", { params }),
            ]);
            setStats(statsRes.data);
            setReviews(reviewsRes.data);
        } catch (err) {
            if (err.response?.status !== 401) {
                toast.error(formatApiError(err));
            }
        } finally {
            setLoading(false);
        }
    };

    const setStatus = async (id, status) => {
        try {
            await authApi.patch(`/admin/reviews/${id}`, { status });
            toast.success(
                status === "approved"
                    ? "Avis approuvé et publié"
                    : status === "rejected"
                    ? "Avis rejeté"
                    : "Statut mis à jour",
            );
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement cet avis ?")) return;
        try {
            await authApi.delete(`/admin/reviews/${id}`);
            toast.success("Avis supprimé");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const logout = () => {
        clearToken();
        nav("/admin/login");
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] text-ink">
            <header className="bg-ink text-white sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-signal font-bold">
                            Carvaelis
                        </div>
                        <h1 className="font-display font-black text-xl tracking-tighter">
                            Espace admin
                        </h1>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-wider font-bold"
                    >
                        <LogOut className="w-4 h-4" />
                        Déconnexion
                    </button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    {stats && [
                        { label: "Total", v: stats.total, cls: "bg-white text-ink" },
                        {
                            label: "En attente",
                            v: stats.pending,
                            cls: "bg-amber-50 border border-amber-200 text-amber-900",
                        },
                        {
                            label: "Approuvés",
                            v: stats.approved,
                            cls: "bg-emerald-50 border border-emerald-200 text-emerald-900",
                        },
                        {
                            label: "Moyenne",
                            v: stats.average_rating ? `${stats.average_rating}/5` : "—",
                            cls: "bg-ink text-white",
                        },
                    ].map((s, i) => (
                        <div key={i} className={`${s.cls} p-6`}>
                            <div className="text-[11px] uppercase tracking-widest font-bold opacity-70">
                                {s.label}
                            </div>
                            <div className="font-display font-black text-3xl mt-2">
                                {s.v}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                    <Filter className="w-4 h-4 text-slate-500" />
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            data-testid={`admin-filter-${f.key}`}
                            className={`px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
                                filter === f.key
                                    ? "bg-ink text-white"
                                    : "bg-white text-slate-600 border border-slate-200 hover:border-ink hover:text-ink"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Reviews list */}
                {loading ? (
                    <div className="py-20 flex justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-signal" />
                    </div>
                ) : reviews.length === 0 ? (
                    <div className="text-center py-20 border border-dashed border-slate-300 bg-white">
                        <Clock className="w-8 h-8 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">
                            Aucun avis dans cette catégorie.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {reviews.map((r) => (
                            <div
                                key={r.id}
                                data-testid={`admin-review-${r.id}`}
                                className="bg-white border border-slate-200 p-6"
                            >
                                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <span className="font-display font-bold text-lg">
                                                {r.name}
                                            </span>
                                            <StatusBadge s={r.status} />
                                            {r.role && (
                                                <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 px-2 py-1">
                                                    {r.role}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex gap-0.5">
                                                {Array.from({ length: 5 }).map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        className={`w-4 h-4 ${
                                                            i < r.rating
                                                                ? "fill-signal text-signal"
                                                                : "text-slate-200"
                                                        }`}
                                                        strokeWidth={1.5}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-xs text-slate-400">
                                                {new Date(r.created_at).toLocaleString(
                                                    "fr-FR",
                                                    {
                                                        day: "numeric",
                                                        month: "short",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    },
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-slate-700 leading-relaxed mb-5 whitespace-pre-line">
                                    {r.message}
                                </p>

                                <div className="flex gap-2 flex-wrap">
                                    {r.status !== "approved" && (
                                        <button
                                            onClick={() => setStatus(r.id, "approved")}
                                            data-testid={`admin-approve-${r.id}`}
                                            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                                        >
                                            <Check className="w-4 h-4" />
                                            Approuver
                                        </button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button
                                            onClick={() => setStatus(r.id, "rejected")}
                                            data-testid={`admin-reject-${r.id}`}
                                            className="inline-flex items-center gap-2 bg-slate-200 hover:bg-slate-300 text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                            Rejeter
                                        </button>
                                    )}
                                    {r.status !== "pending" && (
                                        <button
                                            onClick={() => setStatus(r.id, "pending")}
                                            className="inline-flex items-center gap-2 bg-amber-100 hover:bg-amber-200 text-amber-900 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                                        >
                                            <Clock className="w-4 h-4" />
                                            Remettre en attente
                                        </button>
                                    )}
                                    <button
                                        onClick={() => remove(r.id)}
                                        data-testid={`admin-delete-${r.id}`}
                                        className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ml-auto"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Supprimer
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
